import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Popup } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { buildDashboardCoupledUIEventUpdate } from '../../../utils/eventConfigTransform';
import {
  CouplingHeaderRow,
  CouplingDescriptionWrapper,
  LinkedBadge
} from '../AlarmThresholdCoupling/styled';
import CouplingToggle from '../AlarmThresholdCoupling/CouplingToggle';
import CouplingConfirmAlert from '../CouplingConfirmAlert';

const EnabledNoteText = styled.span`
  font-size: 0.9em;
  color: ${props => props.theme.colors.darkGray};
`;

const DashboardTemperatureCouplingControl = ({
  coupledToDashboard,
  dashboardCouplingEligible,
  dashboardCouplingReason,
  dashboardLimits,
  fetchingEligibility,
  onChange,
  alarmCouplingEnabled,
  otherCouplingActive = false,
  embedded = false
}) => {
  const { t } = useTranslation();
  const [confirmDialog, setConfirmDialog] = useState(null);

  const applyToggle = useCallback((enabling) => {
    if (enabling) {
      onChange(buildDashboardCoupledUIEventUpdate(dashboardLimits));
    } else {
      onChange({
        dashboard_coupling_config: null
      });
    }
  }, [onChange, dashboardLimits]);

  const handleToggle = useCallback(() => {
    const enabling = !coupledToDashboard;
    if (enabling) {
      const messageKey = alarmCouplingEnabled
        ? 'sensors.eventDetection.dashboardCoupling.confirmSwitchFromAlarm'
        : 'sensors.eventDetection.dashboardCoupling.confirmEnable';
      const titleKey = alarmCouplingEnabled
        ? 'sensors.eventDetection.dashboardCoupling.confirmSwitchTitle'
        : 'sensors.eventDetection.dashboardCoupling.confirmEnableTitle';
      setConfirmDialog({
        title: t(titleKey),
        message: t(messageKey)
      });
      return;
    }
    if (coupledToDashboard) {
      setConfirmDialog({
        title: t('sensors.eventDetection.dashboardCoupling.confirmDisableTitle'),
        message: t('sensors.eventDetection.dashboardCoupling.confirmDisable'),
        secondaryMessage: t(
          'sensors.eventDetection.dashboardCoupling.confirmDisableSecondary'
        )
      });
      return;
    }
    applyToggle(enabling);
  }, [coupledToDashboard, alarmCouplingEnabled, applyToggle, t]);

  const handleConfirmProceed = useCallback(() => {
    const enabling = !coupledToDashboard;
    setConfirmDialog(null);
    applyToggle(enabling);
  }, [coupledToDashboard, applyToggle]);

  let disabled = fetchingEligibility;
  let disabledReason = null;
  if (coupledToDashboard) {
    // Already coupled: allow turning off even when location is no longer eligible.
    disabled = fetchingEligibility;
  } else if (alarmCouplingEnabled || otherCouplingActive) {
    disabled = true;
    disabledReason = t('sensors.eventDetection.couplingSource.disableOtherFirstDashboard');
  } else if (!dashboardCouplingEligible) {
    disabled = true;
    if (dashboardCouplingReason === 'not_on_temperature_dashboard') {
      disabledReason = t(
        'sensors.eventDetection.dashboardCoupling.notOnDashboard'
      );
    } else {
      disabledReason = t(
        'sensors.eventDetection.dashboardCoupling.notEligible'
      );
    }
  }

  const linkedBadge = coupledToDashboard ? (
    <LinkedBadge>
      <span>{t('sensors.eventDetection.coupling.linked')}</span>
    </LinkedBadge>
  ) : null;

  const toggleRow = (
    <CouplingHeaderRow>
      <CouplingToggle
        enabled={coupledToDashboard}
        fetchingAlarms={fetchingEligibility}
        onToggle={handleToggle}
        disabled={disabled}
        disabledReason={disabledReason}
        statusTooltip={null}
        labelKey="sensors.eventDetection.dashboardCoupling.coupleWithDashboard"
        toggleHelpKey="sensors.eventDetection.dashboardCoupling.toggleHelp"
      />
      {linkedBadge}
    </CouplingHeaderRow>
  );

  const enabledNote = coupledToDashboard && (
    <CouplingDescriptionWrapper style={{ marginTop: embedded ? '8px' : '12px' }}>
      <Popup
        trigger={(
          <EnabledNoteText>
            {t('sensors.eventDetection.dashboardCoupling.enabledNote')}
          </EnabledNoteText>
        )}
        content={t('sensors.eventDetection.dashboardCoupling.enabledNote')}
        position="bottom left"
      />
    </CouplingDescriptionWrapper>
  );

  return (
    <>
      {confirmDialog && (
        <CouplingConfirmAlert
          title={confirmDialog.title}
          message={confirmDialog.message}
          secondaryMessage={confirmDialog.secondaryMessage}
          onProceed={handleConfirmProceed}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
      {toggleRow}
      {enabledNote}
    </>
  );
};

DashboardTemperatureCouplingControl.propTypes = {
  coupledToDashboard: PropTypes.bool.isRequired,
  dashboardCouplingEligible: PropTypes.bool.isRequired,
  dashboardCouplingReason: PropTypes.string,
  dashboardLimits: PropTypes.object,
  fetchingEligibility: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  alarmCouplingEnabled: PropTypes.bool.isRequired,
  otherCouplingActive: PropTypes.bool,
  embedded: PropTypes.bool
};

DashboardTemperatureCouplingControl.defaultProps = {
  dashboardCouplingReason: null,
  dashboardLimits: null,
  fetchingEligibility: false,
  otherCouplingActive: false,
  embedded: false
};

export default DashboardTemperatureCouplingControl;
